import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { useLanguageStore } from '../store/languageStore';
import { useThemeStore } from '../store/themeStore';
import { Lock, User, Loader2, Moon, Sun, HelpCircle } from 'lucide-react';
import { loginWorker } from '../utils/api';
import { AuthResponse } from '../types/auth';
import { setWorkerSession, checkWorkerSession, generateDeviceId } from '../utils/firebase';
import { HelpDialog } from './HelpDialog';
import { LanguageSelector } from './LanguageSelector';
import translations from '../translations';

export function LoginForm() {
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({ username: '', password: '' });
  const [showHelp, setShowHelp] = useState(false);
  const setAuth = useAuthStore((state) => state.setAuth);
  const { isDark, toggle } = useThemeStore();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguageStore();
  const t = translations[language];

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark'); 
    }
  }, [isDark]);

  const validateForm = () => {
    const newErrors = { username: '', password: '' };
    let isValid = true;

    if (!credentials.username.trim()) {
      newErrors.username = t.signIn.usernameRequired;
      isValid = false;
    }

    if (!credentials.password.trim()) {
      newErrors.password = t.signIn.passwordRequired;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !validateForm()) return;
    
    setLoading(true);
    try {
      if (!credentials.username || !credentials.password) {
        throw new Error('Please enter both username and password');
      }

      const response: AuthResponse = await loginWorker(credentials);

      if (!response.token || !response.worker_id || !response.worker_name || !response.worker_info) {
        throw new Error('Invalid login response from server');
      }

      // Generate a unique device ID for this session
      const deviceId = generateDeviceId();

      // Check for existing session
      const existingSession = await checkWorkerSession(response.worker_id);
      if (existingSession?.is_logged_in && existingSession?.device_id !== deviceId) {
        toast.error('You have been logged out from another device', { duration: 5000 });
      }

      // Set worker session in Firebase with device ID
      await setWorkerSession({
        username: credentials.username,
        worker_id: response.worker_id,
        is_logged_in: true,
        last_active: Date.now(),
        device_id: deviceId
      });

      setAuth(
        response.token,
        response.accessToken,
        response.refreshToken,
        response.worker_id,
        response.worker_name,
        response.worker_info,
        deviceId,
        response.tenantId
      );
      
      toast.success(`Welcome back, ${response.worker_name}!`, {
        duration: 2000,
        icon: '👋',
      });
      navigate('/');
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Invalid credentials. Please try again.', {
        duration: 3000,
      });
      setCredentials(prev => ({ ...prev, password: '' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-900 dark:to-primary-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-repeat opacity-5" style={{ 
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
      }} />
      
      <div className="card w-full max-w-md relative backdrop-blur-sm bg-white/90 dark:bg-dark-card/90 border-2">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={() => setShowHelp(true)}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-border transition-colors"
            aria-label="Get help"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
          <button
            onClick={toggle}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-border transition-colors"
            aria-label="Toggle theme"
          >
            {isDark ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </button>
        </div>

        <div className="p-8">
          <div className="text-center mb-12">
            <div className="absolute top-4 left-4">
              <LanguageSelector />
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-6">
              <svg className="w-10 h-10 text-primary-600 dark:text-primary-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" fill="currentColor"/>
                <path d="M20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t.signIn.title}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {t.signIn.subtitle}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.signIn.username}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  id="username"
                  value={credentials.username}
                  onChange={(e) =>
                    setCredentials({ ...credentials, username: e.target.value })
                  }
                  className={`w-full rounded-lg border bg-white dark:bg-dark-card pl-10 py-3 transition-all focus:ring-2 ${
                    errors.username ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  required
                  disabled={loading}
                  autoComplete="username"
                  placeholder="Enter your username"
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.username}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.signIn.password}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="password"
                  id="password"
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials({ ...credentials, password: e.target.value })
                  }
                  className={`w-full rounded-lg border bg-white dark:bg-dark-card pl-10 py-3 transition-all focus:ring-2 ${
                    errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                t.signIn.button
              )}
            </button>
          </form>
        </div>
      </div>
      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} />}
    </div>
  );
}